import type { ErrorComponentProps } from '@tanstack/react-router'
import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { AlertTriangle, Home, RefreshCw } from 'lucide-react'
import { Sentry, isSentryInitialized } from '@/lib/sentry'

/**
 * Route-level error boundary for TanStack Router.
 * Registered as errorComponent on the root route.
 */
export function RouteErrorBoundary({ error }: ErrorComponentProps) {
  // Report error to Sentry
  useEffect(() => {
    if (isSentryInitialized() && error) {
      Sentry.captureException(error)
    }
  }, [error])

  // Check if this is a chunk loading error
  const isChunkError =
    error instanceof Error &&
    (error.message.includes('Failed to fetch dynamically imported module') ||
     error.message.includes('Loading chunk') ||
     error.message.includes('Importing a module script failed') ||
     error.message.includes('error loading dynamically imported module'))

  const handleReload = () => {
    // Clear cache if it's a chunk error to ensure fresh assets
    if (isChunkError && 'caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name)
        })
        window.location.reload()
      })
    } else {
      window.location.reload()
    }
  }

  const handleGoHome = () => {
    window.location.href = '/'
  }

  // Chunk loading error UI (deployment update)
  if (isChunkError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10">
            <AlertTriangle className="w-8 h-8 text-accent" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">
              Update Available
            </h1>
            <p className="text-muted-foreground">
              A new version of the application has been deployed. Please reload the page to get the latest version.
            </p>
          </div>

          <Button onClick={handleReload} variant="default" size="lg">
            <RefreshCw className="w-4 h-4 mr-2" />
            Reload Page
          </Button>
        </div>
      </div>
    )
  }

  // Generic error UI
  const errorMessage =
    error instanceof Error ? error.message
    : typeof error === 'string' ? error
    : 'An unexpected error occurred.'

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10">
          <AlertTriangle className="w-8 h-8 text-destructive" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            Something went wrong
          </h1>
          <p className="text-muted-foreground">
            {errorMessage}
          </p>
        </div>

        {import.meta.env.DEV && error instanceof Error && (
          <div className="text-left p-4 bg-muted rounded-lg overflow-auto max-h-48">
            <p className="text-sm font-mono text-destructive break-all">
              {error.message}
            </p>
            {error.stack && (
              <pre className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap">
                {error.stack}
              </pre>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
          <Button onClick={handleReload} variant="default">
            <RefreshCw className="w-4 h-4 mr-2" />
            Reload Page
          </Button>
          <Button onClick={handleGoHome} variant="outline">
            <Home className="w-4 h-4 mr-2" />
            Go Home
          </Button>
        </div>
      </div>
    </div>
  )
}
